<template>
    <div class="card shadow-sm border-0">
        <div class="card-header bg-white py-3">
            <div class="d-flex align-items-center justify-content-between">
                <div>
                    <h5 class="card-title fw-bold mb-0 text-primary">
                        <i class="fa-solid fa-calendar-check me-2"></i>Status Luni pentru Lucru
                    </h5>
                    <p class="text-muted small mb-0">Gestionare perioadă de upload documente</p>
                </div>
                <div class="d-flex gap-2">
                    <select v-model="selectedYear" class="form-select form-select-sm" @change="fetchStatuses"
                        style="width: 100px;">
                        <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
                    </select>
                    <button class="btn btn-sm btn-light-primary" @click="fetchStatuses" :disabled="loading">
                        <i class="fa-solid fa-rotate" :class="{ 'fa-spin': loading }"></i>
                    </button>
                </div>
            </div>
        </div>
        <div class="card-body">
            <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status"></div>
                <p class="mt-2 text-muted">Se încarcă statusurile...</p>
            </div>

            <div v-else class="row g-3">
                <div v-for="m in filteredMonths" :key="m" class="col-12 col-md-6 col-lg-4 col-xl-3">
                    <div class="month-card border rounded p-3 h-100 position-relative" :class="getCardClass(m)">
                        <div class="d-flex justify-content-between align-items-start mb-2">
                            <h6 class="fw-bold mb-0 text-uppercase small text-muted">{{ getMonthName(m) }} {{
                                selectedYear }}</h6>
                            <span class="badge rounded-pill" :class="getBadgeClass(m)">
                                {{ getStatusLabel(m) }}
                            </span>
                        </div>

                        <div class="month-info mb-3 small">
                            <div v-if="getStatus(m) === 2" class="text-warning mb-1">
                                <i class="fa-solid fa-circle-exclamation me-1"></i>Clientul a solicitat închiderea
                            </div>
                            <div v-if="getStatus(m) === 3" class="text-danger mb-1">
                                <i class="fa-solid fa-lock me-1"></i>Luna este blocată la upload
                            </div>
                            <div v-else class="text-success mb-1">
                                <i class="fa-solid fa-lock-open me-1"></i>Upload permis
                            </div>
                        </div>

                        <div class="month-actions d-grid mt-auto">
                            <button v-if="getStatus(m) !== 3" class="btn btn-sm btn-outline-danger fw-bold"
                                @click="updateStatus(m, 3)" :disabled="updating === m">
                                <i class="fa-solid fa-lock me-1"></i>Închide Luna
                            </button>
                            <button v-else class="btn btn-sm btn-outline-success fw-bold" @click="updateStatus(m, 1)"
                                :disabled="updating === m">
                                <i class="fa-solid fa-lock-open me-1"></i>Redeschide Luna
                            </button>
                        </div>

                        <div v-if="updating === m"
                            class="updating-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75 rounded">
                            <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Admin_GestionareLuni',
    props: {
        firmaId: {
            type: [Number, String],
            required: true
        }
    },
    data() {
        return {
            loading: false,
            updating: null,
            selectedYear: new Date().getFullYear(),
            statuses: [], // Array<{luna, an, status, ...}>
            years: [],
            firmCreatedAt: null
        }
    },
    computed: {
        filteredMonths() {
            if (!this.firmCreatedAt) return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
            const start = new Date(this.firmCreatedAt)
            const startYear = start.getFullYear()
            const startMonth = start.getMonth() + 1
            const now = new Date()
            const maxDate = new Date(now.getFullYear(), now.getMonth() + 4, 1)
            const maxYear = maxDate.getFullYear()
            const maxMonth = maxDate.getMonth() + 1

            const months = []
            for (let m = 1; m <= 12; m++) {
                if (this.selectedYear === startYear && m < startMonth) continue
                if (this.selectedYear === maxYear && m > maxMonth) continue
                months.push(m)
            }
            return months
        }
    },
    mounted() {
        this.fetchStatuses()
    },
    methods: {
        async fetchStatuses() {
            if (!this.firmaId) return
            this.loading = true
            try {
                const { data } = await axios.get('/api/administrare/work/fisiere/status_luni', {
                    params: {
                        firma_id: this.firmaId,
                        an: this.selectedYear
                    }
                })
                this.statuses = data.data || []
                if (data.firm_created_at) {
                    this.firmCreatedAt = data.firm_created_at
                    this.generateYears()
                }
            } catch (e) {
                console.error(e)
                Swal.fire('Eroare', 'Nu am putut prelua statusul lunilor.', 'error')
            } finally {
                this.loading = false
            }
        },
        generateYears() {
            const now = new Date()
            const maxYear = new Date(now.getFullYear(), now.getMonth() + 4, 1).getFullYear()
            let startYear = now.getFullYear() - 1
            if (this.firmCreatedAt) {
                startYear = new Date(this.firmCreatedAt).getFullYear()
            }
            const y = []
            for (let i = startYear; i <= maxYear; i++) y.push(i)
            this.years = y
        },
        async updateStatus(luna, status) {
            this.updating = luna
            try {
                await axios.post('/api/administrare/work/fisiere/update_status_luna', {
                    firma_id: this.firmaId,
                    luna: luna,
                    an: this.selectedYear,
                    status: status
                })
                await this.fetchStatuses()
                const msg = status === 3 ? 'Luna a fost închisă.' : 'Luna a fost redeschisă.'
                Swal.fire({
                    icon: 'success',
                    title: 'Succes',
                    text: msg,
                    toast: true,
                    position: 'top-end',
                    timer: 3000,
                    showConfirmButton: false
                })
            } catch (e) {
                console.error(e)
                const errMsg = e.response?.data?.message || 'Eroare la actualizarea statusului.'
                Swal.fire('Eroare', errMsg, 'error')
            } finally {
                this.updating = null
            }
        },
        getStatus(luna) {
            const s = this.statuses.find(x => x.luna === luna)
            return s ? s.status : 1 // Implicit Deschis (1)
        },
        getStatusLabel(luna) {
            const s = this.getStatus(luna)
            if (s === 1) return 'Deschisă'
            if (s === 2) return 'Cerere Închidere'
            if (s === 3) return 'Închisă'
            return 'Nespecificat'
        },
        getCardClass(luna) {
            const s = this.getStatus(luna)
            if (s === 3) return 'border-danger-subtle bg-danger-light'
            if (s === 2) return 'border-warning-subtle bg-warning-light'
            return 'border-success-subtle bg-success-light'
        },
        getBadgeClass(luna) {
            const s = this.getStatus(luna)
            if (s === 3) return 'bg-danger text-white'
            if (s === 2) return 'bg-warning text-dark'
            return 'bg-success text-white'
        },
        getMonthName(m) {
            const names = [
                'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
                'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
            ]
            return names[m - 1]
        }
    },
    watch: {
        firmaId() {
            this.fetchStatuses()
        }
    }
}
</script>

<style scoped>
.month-card {
    transition: transform 0.2s, box-shadow 0.2s;
}

.month-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
}

.bg-danger-light {
    background-color: rgba(220, 53, 69, 0.05);
}

.bg-warning-light {
    background-color: rgba(255, 193, 7, 0.05);
}

.bg-success-light {
    background-color: rgba(25, 135, 84, 0.05);
}

.updating-overlay {
    z-index: 5;
}
</style>
